<template>
  <div>

    <loading-indicator v-if="isLoading"></loading-indicator>

    <p v-if="message !== null">
      {{message}}
    </p>

    <template v-if="isDataLoaded && billing.length > 0" v-once>

      <p class="mb-2">
        Количество платежей: {{ paymentAmount | getFormattedNumber }}
      </p>

    <table class="table table-bordered billing-table">
      <thead class="thead-dark">
        <tr class="text-center">
          <th colspan="5">Платеж</th>
          <th colspan="8">Игрок</th>
          <th colspan="7">Товар</th>
        </tr>
        <tr>
          <th scope="col">Ид</th>
          <th scope="col"><span id="col-n">№</span></th>
          <th scope="col">Время</th>
          <th scope="col">Магазин</th>
          <th scope="col">Инфо</th> <!-- Extra info -->
          <th scope="col">Клиент</th>
          <th scope="col">RFM</th>
          <th scope="col">
            <span v-b-popover.hover.top="'Уровень (level)'">
              Lev
            </span>
          </th>
          <th scope="col">
            <span v-b-popover.hover.top="'Платформа (platform)'">
              Platf
            </span>
          </th>
          <th scope="col">
            <!-- Since reg -->
            <span v-b-popover.hover.top="'Время с момента регистрации игрока'">
              Age
            </span>
          </th>
          <th scope="col" colspan="2" class="text-center">
            <span v-b-popover.hover.top="'Монеты. Покупка (paid) / бонус (bonus)'">
              Coins p/b
            </span>
          </th>
          <th scope="col">Gross</th>
          <th scope="col">
            <span v-b-popover.hover.top="'Таблица (table)'">
              T
            </span>
          </th>
          <th scope="col">Service</th>
          <th scope="col">Price USD</th>
          <th scope="col">Curr</th>
          <th scope="col">Coins</th>
          <th scope="col">Products</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="item in billing">
        <tr :key="item.id">
          <th scope="row" class="text-right">{{ item.Id }}</th>
          <td class="text-right">{{ item.PaymentsCount }}</td>
          <td class="text-nowrap">{{ item.Ctime | getMomentStr }}</td>
          <td>
            <span v-b-popover.hover.top="getShopName(item.Shop)">
              {{ item.Shop | getShortShopName }}
            </span>
          </td>
          <td>
            <!-- order id -->
            <span
              class="tooltip-button"
              v-b-popover.click.top="item.TxId"
            >
              order
            </span>
          </td>
          <td>{{ item.ClientVersion | getOptionalStringDefaultStr('?') }}</td>
          <td>{{ item.Rfm | getPlayerRFMCategoryNameShort }}</td>
          <td class="text-right">
            {{ item.PlayerLevel | getOptionalNumberStr('?') }}
          </td>
          <td>{{ item.Platform | getPlatformName }}</td>
          <td>{{ item.Ctime - regTime | getDurationStrForBilling }}</td>
          <td class="text-right">{{ item.GemsPaid }}</td>
          <td class="text-right">{{ item.GemsBonus }}</td>
          <td class="text-right">{{ item.PlayerGrossCent | convertCentToString }}</td>
          <td>
            <span v-b-popover.hover.top="convertBillingTableAliasToName(item.TableId)">
              {{ item.TableId | convertBillingTableAliasToAbbr }}
            </span>
          </td>
          <td>{{ item.Service | getShortBillingServiceId }}</td>
          <td class="text-right">
            {{ item.PriceCent | convertCentToString }}
          </td>
          <td>{{ item.UserCurrency }}</td>
          <td class="text-right">{{ '+' + item.Gems }}</td>
          <td>
            <span
              v-if=isProductsButtonVisible(item.TableId)
              :id="getProductsButtonId(item.Id)"
              class="tooltip-button products-button"
              @click='handleProductsClick(item)'
            >
              products
            </span>
          </td>
        </tr>
        </template>
      </tbody>
    </table>

    <b-tooltip target="col-n" triggers="hover">
      Player payments count<br>
      (including current payment)
    </b-tooltip>

    </template>

    <template v-if="isProductsPopoverShowing">
      <b-popover
        show
        :target="productsButtonId"
        placement="top"
        triggers="manual"
      >
        <billing-products-table
          :billingRecord="currentItem"
          :productGroups="currentBillingItemInfoProducts"
        />
      </b-popover>
    </template>

  </div>
</template>

<script>
import { HTTP } from '../http-common';
import notifier from '../notifier';
import LoadingIndicator from './LoadingIndicator.vue';
import BillingProductsTable from './BillingProductsTable.vue';

import { convertCentToString } from '../money.js';

import { addTableFragmentObjectToTableMap } from '../model/billing.js';
import { findBillingItemInfoProducts      } from '../model/billing.js';
import { getShopName                      } from '../model/billing.js';
import { getShortShopName                 } from '../model/billing.js';
import { convertBillingTableAliasToName   } from '../model/billing.js';
import { convertBillingTableAliasToAbbr   } from '../model/billing.js';

import { isProductsButtonVisible } from '../view/billing.js';
import { mustHidePopover         } from '../view/billing.js';

export default {
  name: 'PlayerBilling',

  components: {
    LoadingIndicator,
    BillingProductsTable,
  },

  props: {
    playerId: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      isLoading: false,
      regTime: null,
      billing: null,
      tableMap: new Map(),

      productsButtonId: null,
      isProductsPopoverShowing: false,
      currentItem: null,
    };
  },

  computed: {
    isDataLoaded() {
      return this.billing !== null;
    },

    message() {
      if(this.billing !== null && this.billing.length === 0) {
        return 'This player has no payments.';
      }
      return null;
    },

    currentBillingItemInfoProducts() {
      return findBillingItemInfoProducts(this.tableMap, this.currentItem);
    },

    /** @return {number} */
    paymentAmount() {
      return this.billing.length;
    },
  },

  created() {
    document.addEventListener('keydown', this.escapeKeyHandler);
    document.body.parentNode.addEventListener('mousedown', this.globalMousedownHandler);
    this.loadBilling();
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.escapeKeyHandler);
    document.body.parentNode.removeEventListener('mousedown', this.globalMousedownHandler);
  },

  methods: {
    loadBilling() {
      this.isLoading = true;
      HTTP.get('/player/' + this.playerId + '/billing')
      .then(response => {
        const data = response.data.data;

        this.regTime = data.regTime;
        addTableFragmentObjectToTableMap(this.tableMap, data.billingTableInfoMap);

        const billing = [];
        for(let i = 0; i < data.iaps.length; i++) {
          const iap = data.iaps[i];
          billing.push(iap);
        }
        billing.sort((a, b) => b.Id - a.Id);
        this.billing = billing;
      })
      .catch(err => {
        notifier.showError(err);
      })
      .finally(() => {
        this.isLoading = false;
      });
    },

    isProductsButtonVisible,

    handleProductsClick(item) {
      if(!this.isProductsPopoverShowing) {
        this.currentItem = item;
        const productsButtonId = this.getProductsButtonId(item.Id);
        this.productsButtonId = productsButtonId;
        this.isProductsPopoverShowing = true;
        return;
      }

      if(this.isProductsPopoverShowing && item === this.currentItem) {
        this.stopProductsPopoverShowing();
        return;
      }

      if(this.isProductsPopoverShowing && item !== this.currentItem) {
        this.isProductsPopoverShowing = false;
        this.currentItem = item;
        this.productsButtonId = this.getProductsButtonId(item.Id);
        this.$nextTick(function() {
          this.isProductsPopoverShowing = true;
        });
        return;
      }
    },

    getProductsButtonId(billingRecordId) {
      return 'btn-' + billingRecordId + '-products';
    },

    escapeKeyHandler(event) {
      if(event.keyCode === 27 && this.isProductsPopoverShowing) {
        this.stopProductsPopoverShowing();
      }
    },

    globalMousedownHandler(event) {
      if(mustHidePopover(event.target)) {
        this.stopProductsPopoverShowing();
      }
    },

    stopProductsPopoverShowing() {
      this.isProductsPopoverShowing = false;
      this.productsButtonId = null;
      this.currentItem = null;
    },

    getShopName,
    convertBillingTableAliasToName,
  },

  filters: {
    getShortShopName,
    convertCentToString,
    convertBillingTableAliasToAbbr,
  },
}
</script>

<style scoped>
  .table {
    white-space: nowrap;
  }

  .tooltip-button {
    text-decoration: underline;
    cursor: pointer;
  }

  .popover {
    max-width: 800px;
  }
</style>
